import React from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
  Divider,
  Paper,
} from "@mui/material";
import TextInputField from "../../TextInputs/TextInputField";
import ActionButton, { DarkGreyButton } from "../../Buttons/ActionButton";
import {
  Close,
  InfoOutlined,
  Password,
  Cookie,
  Person,
  Email,
  AdminPanelSettings,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import CloseButton from "../../Buttons/CloseButton";
import {
  checkUserDetailsFromCookie,
  resetUserPassword,
} from "../../../../api_calls/userManagement";
import { HtmlTooltip } from "../../StyledComponents/styled";
import CookieDetailModal from "../../CookieBanner/CookieDetailModal";

const ResetPassword = ({ open, onClose, size }) => {
  const currentStateData = useSelector((state) => state.userSelections);
  const [showConfirmation, setshowConfirmation] = React.useState(false);
  const [cookieModalOpen, setCookieModalOpen] = React.useState(false);
  let threshold = 5;
  const [formData, setFormData] = React.useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [userData, setUserData] = React.useState({});

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleResetPassword = (e) => {
    e.preventDefault();
    if (!formData.newPassword || !formData.confirmPassword) {
      toast("Please fill in all fields.", { type: "error" });
    } else if (formData.newPassword !== formData.confirmPassword) {
      toast("New password and confirm password do not match.", { type: "error" });
    } else {
      setshowConfirmation(true);
    }
  };

  const updatePassword = () => {
    resetUserPassword(formData.newPassword);
    onClose();
  };

  const handleOpenCookieSettings = () => {
    setCookieModalOpen(true);
  };

  const handleCloseCookieSettings = () => {
    setCookieModalOpen(false);
  };

  const RoleWithTooltipDescription = ({ role }) => {
    return (
      <Box
        sx={{
          mb: 1,
          p: 1,
          borderRadius: 1,
          backgroundColor: "#f8f9fa",
          border: "1px solid #e9ecef",
        }}
      >
        <Stack direction={"row"} spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {role.role_name}
          </Typography>
          <HtmlTooltip
            title={
              <Box width={300}>
                <Typography variant="body2">{role.description}</Typography>
              </Box>
            }
          >
            <InfoOutlined sx={{ fontSize: "15px", cursor: "pointer", color: "#AF3241" }} />
          </HtmlTooltip>
        </Stack>
      </Box>
    );
  };

  const renderRoleList = () => {
    const middleIndex = Math.ceil(userData.user_role_info.length / 2);
    const firstColumn = userData.user_role_info.slice(0, middleIndex);
    const secondColumn = userData.user_role_info.slice(middleIndex);

    return (
      <Grid container spacing={2}>
        {userData.user_role_info.length > threshold ? (
          <>
            <Grid item xs={6}>
              {firstColumn.map((role, index) => (
                <RoleWithTooltipDescription key={index} role={role} />
              ))}
            </Grid>
            <Grid item xs={6}>
              {secondColumn.map((role, index) => (
                <RoleWithTooltipDescription key={index} role={role} />
              ))}
            </Grid>
          </>
        ) : (
          <Grid item xs={12}>
            {userData.user_role_info.map((role, index) => (
              <RoleWithTooltipDescription key={index} role={role} />
            ))}
          </Grid>
        )}
      </Grid>
    );
  };

  React.useEffect(() => {
    const checkUserDetail = checkUserDetailsFromCookie();

    if (checkUserDetail.success === true) {
      setUserData(checkUserDetail.user_detail);
    }
    setFormData({
      newPassword: "",
      confirmPassword: "",
    });
    setshowConfirmation(false);
  }, [open]);

  return (
    <>
      <Dialog
        open={open}
        onClose={(e) => {
          e.preventDefault();
        }}
        fullWidth={true}
        maxWidth={"md"}
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6">Account Settings</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {showConfirmation ? (
            <Paper elevation={0} sx={{ p: 3, backgroundColor: "#fff3f4", border: "1px solid #AF3241" }}>
              <Typography variant="body2">
                By confirming this action, your password will be updated, and you will be logged out. You will need to log
                in again with your new password to continue using the system.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={3}>
              {/* User Information Section */}
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <Person sx={{ color: "#AF3241" }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 2, color: "#333" }}>
                    User Information
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  {typeof userData.user_name !== "undefined" && (
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="body2" sx={{ minWidth: 80, color: "#666" }}>
                        Username
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {userData.user_name}
                      </Typography>
                    </Stack>
                  )}
                  {typeof userData.user_email !== "undefined" && (
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="body2" sx={{ minWidth: 80, color: "#666" }}>
                        E-Mail
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {userData.user_email}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>

              {/* Roles Section */}
              {typeof userData.assigned_user_roles !== "undefined" &&
                typeof userData.user_role_info !== "undefined" &&
                !userData.assigned_user_roles.includes("Brand") && (
                  <>
                    <Divider />
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                        <AdminPanelSettings sx={{ color: "#AF3241", fontSize: 20 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#333" }}>
                          Assigned Roles
                        </Typography>
                      </Stack>
                      {renderRoleList()}
                    </Box>
                  </>
                )}

              {/* Change Password Section */}
              <Divider />
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <Password sx={{ color: "#AF3241", fontSize: 20 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#333" }}>
                    Change Password
                  </Typography>
                </Stack>
                <Stack spacing={2}>
                  <TextInputField
                    fieldRequired={true}
                    elemId="newPassword"
                    label="New Password"
                    fieldName="newPassword"
                    showlabel={true}
                    defaultVal={formData.newPassword}
                    inputType={"password"}
                    onChangeCall={handleInputChange}
                  />
                  <TextInputField
                    fieldRequired={true}
                    elemId="confirmPassword"
                    label="Confirm New Password"
                    fieldName="confirmPassword"
                    showlabel={true}
                    defaultVal={formData.confirmPassword}
                    inputType={"password"}
                    onChangeCall={handleInputChange}
                  />
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <CloseButton
              onClick={onClose}
              label="Cancel"
              icon={<Close />}
            />
            <Stack direction="row" spacing={2}>
              {!showConfirmation ? (
                <DarkGreyButton
                  onClick={handleOpenCookieSettings}
                  label="Manage Cookie Preferences"
                  icon={<Cookie />}
                  sx={{ mt: 0, mb: 0 }}
                />
              ) : null}
              <ActionButton
                onClick={showConfirmation ? updatePassword : handleResetPassword}
                label={showConfirmation ? "Confirm" : "Update"}
                icon={<Password />}
              />
            </Stack>
          </Stack>
        </DialogActions>
      </Dialog>

      <CookieDetailModal
        open={cookieModalOpen}
        onClose={handleCloseCookieSettings}
      />
    </>
  );
};

export default ResetPassword;